import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isEqual, noop } from 'vet-bones/utils';

import { MAX_CSV_PARSE_LINES } from 'src/constants/limits';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
  ProjectFileUploadStatistics,
} from 'src/store/state/projects';
import { assignDistinctAndRecordStatistics } from 'src/utils/csv/assignDistinctAndRecordStatistics';
import {
  ExtendedParseError,
  ParseCsvResult,
  parseCsv,
} from 'src/utils/csv/parseCsv';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';

export const useProjectFileUploadParser = (
  file: string | File | null | undefined,
  fileOptions: ProjectFileUploadOptions,
  limitRows?: number,
  existingFields?: ProjectFileUploadField[]
) => {
  const [errors, setErrors] = React.useState<ExtendedParseError[]>([]);
  const [fields, setFields] = React.useState<ProjectFileUploadField[]>(
    existingFields || []
  );
  const [rows, setRows] = React.useState<string[][]>([]);

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParser = React.useRef<() => void>(noop);

  // Stabilize existingFields to prevent unnecessary re-runs
  const existingFieldsRef = React.useRef<ProjectFileUploadField[]>();
  if (!isEqual(existingFieldsRef.current, existingFields)) {
    existingFieldsRef.current = existingFields;
  }

  React.useEffect(() => {
    didCleanup.current = false;
    abortParser.current();

    if (file && fileOptions.quote && fileOptions.separator) {
      const id = uuid();
      parsingIds.current.add(id);
      setIsParsing(true);
      // Clear previous parse output so a save during re-parse can't pick up
      // stale fields/rows from the previously-parsed file.
      setErrors([]);
      setFields([]);
      setRows([]);
      const { abort, parsePromise } = parseCsv<ProjectFileUploadStatistics>(
        file,
        {
          delimiter: fileOptions.separator,
          escapeChar: fileOptions.escape,
          header: fileOptions.header,
          quoteChar: fileOptions.quote,
        },
        { distinct: 0, records: 0 },
        assignDistinctAndRecordStatistics,
        limitRows
      );

      abortParser.current = abort;
      parsePromise.then(({ data, errors }) => {
        if (didCleanup.current) {
          return;
        }

        if (errors) {
          setErrors(errors);
          setFields([]);
          setRows([]);
        } else if (data) {
          setErrors([]);
          setFields(
            data.fields.map((newField, index) => ({
              ...newField,
              label:
                existingFieldsRef.current?.[index]?.value === newField.value
                  ? existingFieldsRef.current[index].label
                  : newField.label,
            }))
          );
          setRows(data.rows);
        }
        parsingIds.current.delete(id);
        setIsParsing(Boolean(parsingIds.current.size));
      });
    } else if (!didCleanup.current) {
      abortParser.current = noop;
      setFields([]);
      setRows([]);
    }

    return () => {
      didCleanup.current = true;
      abortParser.current();
    };
  }, [file, fileOptions, limitRows]);

  const updateFieldLabel = React.useCallback(
    (value: string, label: string) => {
      setFields(
        fields.map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        }))
      );
    },
    [fields]
  );

  return {
    abortParser,
    errors,
    fields,
    isParsing,
    rows,
    updateFieldLabel,
  };
};

export const useProjectMultipleFileUploadsParser = (
  files: File[],
  fileOptions: ProjectFileUploadOptions
) => {
  const [errorsByFileIdx, setErrorsByFileIdx] = React.useState<
    Record<number, ExtendedParseError[]>
  >({});
  const [fieldsByFileIdx, setFieldsByFileIdx] = React.useState<
    Record<number, ProjectFileUploadField[]>
  >({});
  const [isPartialByFileIdx, setIsPartialByFileIdx] = React.useState<
    Record<number, boolean>
  >({});
  const [rowsByFileIdx, setRowsByFileIdx] = React.useState<
    Record<number, string[][]>
  >({});

  const parsingIds = React.useRef<Set<string>>(new Set());
  const [isParsing, setIsParsing] = React.useState(false);

  const didCleanup = React.useRef<boolean>(false);
  const abortParserFns = React.useRef<Array<() => void>>([]);
  const didFilesChange = useDidValueChange(files, isEqual);
  const didOptionsChange = useDidValueChange(fileOptions, isEqual);

  React.useEffect(() => {
    didCleanup.current = false;
    abortParserFns.current.forEach((abortParser) => abortParser());

    if (didFilesChange || didOptionsChange) {
      if (files.length && fileOptions.quote && fileOptions.separator) {
        const id = uuid();
        parsingIds.current.add(id);
        setIsParsing(true);

        const parsePromises: Array<
          Promise<ParseCsvResult<ProjectFileUploadStatistics>>
        > = [];
        files.forEach((file) => {
          // Parse one row past the preview limit so we can detect whether the
          // file has more rows than we retain (and is therefore partial).
          const { abort, parsePromise } = parseCsv<ProjectFileUploadStatistics>(
            file,
            {
              delimiter: fileOptions.separator,
              escapeChar: fileOptions.escape,
              header: fileOptions.header,
              quoteChar: fileOptions.quote,
            },
            { distinct: 0, records: 0 },
            assignDistinctAndRecordStatistics,
            MAX_CSV_PARSE_LINES + 1
          );
          abortParserFns.current.push(abort);
          parsePromises.push(parsePromise);
        });

        Promise.all(parsePromises).then((results) => {
          const nextErrorsByFileIdx: Record<number, ExtendedParseError[]> = {};
          const nextFieldsByFileIdx: Record<number, ProjectFileUploadField[]> =
            {};
          const nextIsPartialByFileIdx: Record<number, boolean> = {};
          const nextRowsByFileIdx: Record<number, string[][]> = {};
          results.forEach(({ data, errors }, idx) => {
            if (didCleanup.current) {
              return;
            }
            if (errors) {
              // this looks dumb, but it looks like papaparse reuses the same ref for
              // data && errors (so previous errors get cleared if we use the errors arr directly)
              nextErrorsByFileIdx[idx] = [...errors];
              nextFieldsByFileIdx[idx] = [];
              nextRowsByFileIdx[idx] = [];
            } else if (data) {
              // Partial when the file has more rows than the preview we retain.
              // We parsed MAX_CSV_PARSE_LINES + 1 to detect overflow, so keep
              // only the first MAX_CSV_PARSE_LINES rows.
              const isPartial = data.rows.length > MAX_CSV_PARSE_LINES;
              const previewRows = isPartial
                ? data.rows.slice(0, MAX_CSV_PARSE_LINES)
                : data.rows;
              // parseCsv computed statistics over the extra overflow row; when
              // partial, recompute over only the rows we keep so the shown
              // records/distinct match the preview table.
              const previewFields = isPartial
                ? data.fields.map((field) => ({
                    ...field,
                    statistics: { ...field.statistics },
                  }))
                : data.fields;
              if (isPartial) {
                assignDistinctAndRecordStatistics(previewFields, previewRows);
              }
              nextErrorsByFileIdx[idx] = [];
              nextFieldsByFileIdx[idx] = previewFields;
              nextIsPartialByFileIdx[idx] = isPartial;
              nextRowsByFileIdx[idx] = previewRows;
            }
          });
          setErrorsByFileIdx(nextErrorsByFileIdx);
          setFieldsByFileIdx(nextFieldsByFileIdx);
          setIsPartialByFileIdx(nextIsPartialByFileIdx);
          setRowsByFileIdx(nextRowsByFileIdx);
          parsingIds.current.delete(id);
          setIsParsing(Boolean(parsingIds.current.size));
        });
      } else {
        abortParserFns.current = [];
        setFieldsByFileIdx({});
        setRowsByFileIdx({});
        setIsPartialByFileIdx({});
      }
    }

    return () => {
      didCleanup.current = true;
      abortParserFns.current.forEach((abortParser) => abortParser());
    };
  }, [
    didFilesChange,
    didOptionsChange,
    errorsByFileIdx,
    fieldsByFileIdx,
    fileOptions.escape,
    fileOptions.header,
    fileOptions.quote,
    fileOptions.separator,
    files,
    rowsByFileIdx,
    setErrorsByFileIdx,
  ]);

  const updateFieldLabelForFileIdx = React.useCallback(
    (idx: number, value: string, label: string) => {
      if (!fieldsByFileIdx[idx]) {
        return;
      }
      setFieldsByFileIdx({
        ...fieldsByFileIdx,
        [idx]: fieldsByFileIdx[idx].map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        })),
      });
    },
    [fieldsByFileIdx]
  );

  return {
    abortParserFns: abortParserFns.current,
    errorsByFileIdx,
    fieldsByFileIdx,
    isParsing,
    isPartialByFileIdx,
    rowsByFileIdx,
    updateFieldLabelForFileIdx,
  };
};
